/**
 * Created by Ella Ma on 04/26/2019.
 * Description:
 *
 * ------ maintenance history ------
 * 01/08/2020 Ella Ma: code refactor save dashboard.
 * 12/8/2021 Simon Zhao: replaced the direct query with a query subject.
 * 05/20/2022 Simon Zhao merged dashboard-view-sub-chrome-component in.
 */

import { Component, OnInit, Input, ViewChild, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, Subject } from 'rxjs';
import { take, filter, debounceTime, map, switchMap } from 'rxjs/operators';
import * as _lodash from 'lodash';
import { ScreenService } from '../../../../tamalelibs/services/screen.service';
import { DashboardDialogService } from '../../../dashboard/dashboard-dialog/dashboard-dialog.service';
import { SaveScreenDialogOperations } from '../../../save-screen-dialog/save-screen-dialog.view-model';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import {
    DashboardEditorSubChromeConfig, DashboardEditorSubChromeEventType,
    DashboardEditorSubChromeActionType
} from './dashboard-editor-sub-chrome.model';
import { ScreenFocusType } from '../../../../models/saved-screen';
import { EntityService } from '../../../../tamalelibs/services/entity.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../redux';
import { DashboardActionTypes } from '../../../../redux/actions/dashboard.actions';
import { EntityBrief } from '../../../../tamalelibs/models/entity-brief.model';
import { businessConstants } from '../../../../constants/business.constants';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SystemUser } from '../../../../tamalelibs/models/user.model';
import { StoreQuerierService } from '../../../../services/store-querier.service';
import { ContextMenuConfig, ContextMenuShowAction } from '../../../../widgets/tam-context-menu/tam-context-menu.model';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';
import { AlertBtnTypes, AlertButton } from '../../../../widgets/alert-window/alert-window.model';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { SystemActionTypes } from '../../../../tamalelibs/redux/actions/system.actions';
import { HomeScreenActionTypes } from '../../../../redux/actions/home-screen.actions';
import { PrintPreviewDashboardService } from '../../../../pages/print-dashboard/print-preview-dashboard/print-preview-dashboard.service';
import { DashboardPrintSetting, EmailDigestType } from '../../../../tamalelibs/models/emaildigest.model';
import { EmailDigestService } from '../../../../services/email-digest.service';
import { EmailDigestDialogActions } from '../../../email-digest/email-digest.model';
import { TamDashboardTileType } from '../../tam-dashboard.model';
import { ResearchScreenActionTypes } from '../../../../redux/actions/research-screen.actions';
import { Focus } from '../../../dashboard/focus-setting/focus-setting.model';
import { Entity } from '../../../../tamalelibs/models/entity.model';

@Component({
    selector: 'dashboard-editor-sub-chrome',
    templateUrl: './dashboard-editor-sub-chrome.component.html',
    styleUrls: ['./dashboard-editor-sub-chrome-component.scss']
})
export class DashboardEditorSubChromeComponent implements OnInit, OnDestroy {
    @Input() config: DashboardEditorSubChromeConfig;

    contextMenuConfig: ContextMenuConfig;

    finderDS: any;
    @ViewChild('finder', { static: false })
    finderEle: any;
    isFinderLoading: boolean;
    /**
     * a flag indicating whether it's edit view.
     */
    get isEditView(): boolean {
        return this.viewType === 'edit';
    }

    /**
     * a flag indicating whether the view is visible.
     */
    get isViewVisible(): boolean {
        return this.isEditView || ((this.config.fromPage === 'homeScreen' && this.showFocus) || !this.config.fromPage);
    }

    screenInfo: any = {};
    showFocus = false;
    isOneDegreeShown = false;
    isOneDegreeChecked = false;
    showOperateButton = false;
    selectItem: EntityBrief;

    saveBtnLoading = false;

    virtualizationSetting = { itemHeight: 42 };
    @Input()
    viewType: 'view' | 'edit' = 'edit';

    private _currentUser: SystemUser;

    private _destroySubscriptions: Array<Subscription> = [];

    private _filterHandler$: Subject<string> = new Subject();

    private _filterKeywordMap = new Map<string, EntityBrief[]>();

    private _finderValueEmitter$: Subject<EntityBrief> = new Subject();
    /**
     * a flag indicating whether the focus value in store should be cleared on destroying.
     *
     * @private
     * @memberof DashboardEditorSubChromeComponent
     */
    private _isFocusValueNeedToClear = true;
    /**
     * Creates a separate subject to execute queries of focus entities for current keyword against server.
     *
     * @private
     * @memberof DashboardEditorSubChromeComponent
     */
    private _queryEntitySubject$ = new Subject<{ keyword: string, isAutoToggle: boolean }>();
    private _screenData: any = {};
    private _source: any = {};
    private _isManuallyChangedFinder = false;
    private _isIPAD = false;

    constructor(
        private _alertWindow: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _entityService: EntityService,
        private _newDashboardService: DashboardDialogService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _screenService: ScreenService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _toast: ToastService,
        private _deviceService: DeviceDetectorService,
        private _emailDigestService: EmailDigestService,
        private _printPreviewDashboardService: PrintPreviewDashboardService
    ) { }

    ngOnInit() {
        this._destroySubscriptions.push(
            this.config.events$.subscribe(event => this._onSubscribeEvents(event)),
            this._filterHandler$.pipe(
                debounceTime(250),
                filter(filterStr => filterStr !== null && filterStr.length > 0)
            ).subscribe(filterStr => this._filterHandler(filterStr)),
            this._queryEntitySubject$.pipe(
                debounceTime(250),
                map(queryPara => this._entityService.getEntityListBySearchTextShortcut(queryPara.keyword, this._source).pipe(
                    map(response => {
                        return { entities: this._entityService.mapEntityBriefList(response), para: queryPara };
                    }),
                    take(1),
                )),
                switchMap(data => data),
            ).subscribe(data => {
                this._filterKeywordMap.set(data.para.keyword, data.entities);
                this.finderDS = data.entities;
                this.isFinderLoading = false;
                // toggle down the combobox when a new data is obtained.
                if (data.para.isAutoToggle) {
                    this._toggleDropdown();
                }
                this._isManuallyChangedFinder = true;
            }),
            this._finderValueEmitter$.pipe(debounceTime(1000)).subscribe(value => {
                if (!value) {
                    // the selection of dropdown list is cleared, need to repopulate the dropdown with top 200.
                    this._filterHandler('', false);
                } else {
                    this._filterHandler(value && value.shortName ? value.shortName : '', false);
                }

                if (this.isOneDegreeChecked && value) {
                    this._entityService.getEntityDetailWithoutAdhoc(value.id).subscribe(
                        res => {
                            const entities = [value];
                            if (res.relationships) {
                                if (res.relationships.entities) {
                                    Array.from(res.relationships.entities).forEach((rel: { type: {}, children: Array<{}> }) => {
                                        if (rel.children && rel.children.length > 0) {
                                            entities.push(...rel.children.map(e => EntityBrief.parse(e)));
                                        }
                                    });
                                }
                                if (res.relationships.teams) {
                                    Array.from(res.relationships.teams).forEach((rel: { type: {}, children: Array<{}> }) => {
                                        if (rel.children && rel.children.length > 0) {
                                            entities.push(...rel.children.map(e => EntityBrief.parse(e)));
                                        }
                                    });
                                }
                            }
                            this.config.actions$.next({ type: DashboardEditorSubChromeActionType.onFinderValueChange, payload: entities });
                        }
                    );
                } else {
                    this.config.actions$.next({ type: DashboardEditorSubChromeActionType.onFinderValueChange, payload: value ? [value] : null });
                }

                this._setFocusValueStore();
                if (this._isManuallyChangedFinder) {
                    // set homescreen change status for auto-save
                    this._store.dispatch({
                        type: ResearchScreenActionTypes.SET_HOME_SCREEN_CHANGE_STATUS,
                        payload: {
                            screenKey: this.config.storeKey,
                            component: ''
                        }
                    });
                    this._isManuallyChangedFinder = false;
                }
                if (!value) {
                    // the entity selection is reseted, so toggle off the dropdown.
                    this._toggleDropdown(false);
                }
            })
        );

        this._currentUser = this._storeQuerier.getCurrentUser();
        this._isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
        this.showOperateButton = this._currentUser && !!this._currentUser.credential.publishPubConfiguration;
        // if the device is ipad, the edit dashboard button will hide
        if (this._isIPAD) {
            this.showOperateButton = false;
        }

        if (!this.isEditView) {
            this._initMeatBall();
        }
    }

    ngOnDestroy() {
        if (this._isFocusValueNeedToClear) {
            this._store.dispatch({
                type: DashboardActionTypes.SET_FOCUS_VALUE,
                payload: null
            });
        }
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onEditClick() {
        if (this.screenInfo.opType === SaveScreenDialogOperations.COPY) {
            this._screenData.name = this.screenInfo.name;
            this._newDashboardService.open(SaveScreenDialogOperations.COPY, this._screenData).subscribe(data => {
                if (data.payload) {
                    this.screenInfo.name = data.payload.name;
                    this.showFocus = this._isShowFocus(data.payload.configuration.focus.type);
                    this.isOneDegreeShown = this._isShowOneDegree(data.payload.configuration.focus);
                    if (data.payload.configuration.focus.entityTypes && data.payload.configuration.focus.entityTypes.length > 0) {
                        this._source.entityType = data.payload.configuration.focus.entityTypes;
                    }
                    this.config.actions$.next({ type: DashboardEditorSubChromeActionType.refreshScreen, payload: data.payload });
                }
            });
        } else {
            this._newDashboardService.toggleLoading(true);
            this._screenService.getById(this.screenInfo.id).subscribe(screen => {
                let opType = this.screenInfo.opType;
                // on dashbord edit page, opType always is EDIT
                if (opType === SaveScreenDialogOperations.CREATE) {
                    opType = SaveScreenDialogOperations.EDIT;
                }
                this._newDashboardService.open(opType, screen).subscribe(data => {
                    if (data.payload) {
                        this.screenInfo.name = data.payload.name;
                        this.showFocus = this._isShowFocus(data.payload.configuration.focus.type);
                        this.isOneDegreeShown = this._isShowOneDegree(data.payload.configuration.focus);
                        if (data.payload.configuration.focus.entityTypes && data.payload.configuration.focus.entityTypes.length > 0) {
                            this._source.entityType = data.payload.configuration.focus.entityTypes;
                        }
                        this.config.actions$.next({ type: DashboardEditorSubChromeActionType.refreshScreen, payload: data.payload });
                    }
                });
                this._newDashboardService.toggleLoading(false);
            }, () => {
                this._newDashboardService.close();
            });
        }
    }

    onOneDegreeChange() {
        this.isOneDegreeChecked = !this.isOneDegreeChecked;
        if (this.selectItem) {
            this.onFinderValueChange(this.selectItem);
        }
    }

    onDashboardEditClick(info) {
        this._markFlagNotClearFocus();
        let screenId = this.screenInfo ? this.screenInfo.id : null;
        let queryParas = null;
        if (info) {
            screenId = info.screenKey;
            queryParas = {
                queryParams: {
                    id: info.tileId
                }
            };
            this._router.navigate(['dashboard/edit', screenId], queryParas);
        } else {
            this._router.navigate(['dashboard/edit', screenId]);
        }
    }

    onCancel() {
        const subscription = this._alertWindow.warn(StringLiteralsPipe.translate('general.you_have_unsaved'),
            [StringLiteralsPipe.translate('general.sure_leave_page')], StringLiteralsPipe.translate('general.leave'), StringLiteralsPipe.translate('general.stay')).subscribe((result: boolean) => {
                if (result) {
                    this.config.changeRouterBySelf = true;
                    const fromPage = this._route.snapshot.queryParams.from;
                    if (fromPage === 'settings') {
                        // navigated to 'Manage Screen
                        this._router.navigate(['settings', 'screens']);
                    } else {
                        this._setFocusValueStore();
                        this._router.navigate(['dashboard', this.screenInfo.id]);
                    }
                }
                subscription.unsubscribe();
            });
    }

    onSave() {
        this.config.actions$.next({
            type: DashboardEditorSubChromeActionType.onSave
        });
    }

    onFinderFilterChange(filterStr) {
        this._toggleDropdown(false);
        this._filterHandler$.next(filterStr);
    }

    onFinderValueChange(value) {
        this._finderValueEmitter$.next(value);
    }

    onFinderFocus() {
        this._toggleDropdown(false);
        this._filterHandler(!!this.selectItem && this.selectItem.shortName ? this.selectItem.shortName : '', true);
    }

    showContextMenu(e: MouseEvent) {
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(e, ''));
    }

    private _createEmailDigest() {
        const tempId = this.showOperateButton ? this._currentUser.id : null;
        const otherSetting = new DashboardPrintSetting();
        otherSetting.dashboardId = this.screenInfo.id;
        const tempDigest = this._emailDigestService.getDefaultEmailDigest(EmailDigestType.DASHBOARD, tempId);
        tempDigest.reportData = otherSetting;
        // source = {} || {entityType: []}
        this._emailDigestService.dialogActionSubject$.next({
            type: EmailDigestDialogActions.OPEN,
            payload: tempDigest,
            showFocus: this.showFocus,
            focusSource: this._source,
            dashboardName: this.screenInfo.name,
            isGridWidgetIncluded: this.config.screenData && this.config.screenData.configuration && this.config.screenData.configuration.widgets
                && this.config.screenData.configuration.widgets.findIndex(
                    w => w.tile.visualization.type === TamDashboardTileType.GRID) >= 0
        });
    }

    /**
     * deletes current dashboard.
     */
    private _deleteDashboard() {
        const cancelBtn: AlertButton = {
            text: StringLiteralsPipe.translate('general.cancel'),
            type: AlertBtnTypes.tertiary
        };
        const confirmButton: AlertButton = {
            text: StringLiteralsPipe.translate('general.delete'),
            type: AlertBtnTypes.destructive
        };
        const subscription = this._alertWindow.
            custom(StringLiteralsPipe.translate('general.delete_confirmation_required'), [StringLiteralsPipe.translate('dashboard.are_you_sure_delete_dashboard')], cancelBtn, confirmButton)
            .subscribe((result: boolean) => {
                if (result) {
                    this._screenService.deleteById(this.screenInfo.id).pipe(
                        take(1)
                    ).subscribe(() => {
                        this._toast.notify({
                            message: StringLiteralsPipe.translate('general.dashboard_deleted'),
                            style: NotificationStyles.Success
                        });
                        // update left dashboard menu
                        this._store.dispatch({
                            type: SystemActionTypes.DELETE_SCREEN,
                            payload: {
                                id: this.screenInfo.id,
                            }
                        });
                        // reload the homescreen tabs before navigate to home
                        this._store.dispatch({
                            type: HomeScreenActionTypes.LOAD
                        });
                        this._router.navigate(['home']);
                    });
                }
                subscription.unsubscribe();
            });
    }

    private _filterHandler(filterStr: string, isAutoToggle = true) {
        if (!this._filterKeywordMap.has(filterStr)) {
            // for new search string, query with it and cache the result in the map.
            this.isFinderLoading = true;
            // replace the direct query with this subject in order to avoid duplicated requests.
            this._queryEntitySubject$.next({ keyword: filterStr, isAutoToggle: isAutoToggle });
        } else {
            this.finderDS = this._filterKeywordMap.get(filterStr);
            if (isAutoToggle) {
                this._toggleDropdown();
            }
        }
    }

    private _initMeatBall() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.showItemIcon = false;
        this.contextMenuConfig.items = [];
        if (!this._isIPAD) {
            if (this.showOperateButton && !this.config.fromPage) {
                this.contextMenuConfig.items.push(
                    { id: 'copy', text: StringLiteralsPipe.translate('dashboard.copy_dashboard') },
                    { id: 'delete', text: StringLiteralsPipe.translate('dashboard.delete_dashboard') }
                );
            }
            this.contextMenuConfig.items.push(
                { id: 'print', text: StringLiteralsPipe.translate('dashboard.print_dashboard') },
                { id: 'email', text: StringLiteralsPipe.translate('dashboard.new_email_digest') },
            );
        }
        this.contextMenuConfig.events$.subscribe(event => {
            if (event.payload.id === 'copy') {
                this._markFlagNotClearFocus();
                this._router.navigate(['dashboard/copy', this.screenInfo.id]);
            } else if (event.payload.id === 'delete') {
                this._deleteDashboard();
            } else if (event.payload.id === 'print') {
                this._onPreview();
            } else if (event.payload.id === 'email') {
                this._createEmailDigest();
            }
        });
    }

    private _markFlagNotClearFocus() {
        this._isFocusValueNeedToClear = false;
    }

    private _onPreview() {
        // save grid tile current layout to store
        this.config.actions$.next({ type: DashboardEditorSubChromeActionType.saveGridLayout });
        this._printPreviewDashboardService.openDialog(this.screenInfo.name);
    }

    private _setFocusValueStore() {
        this.config.clearStoreFocusValue = false;
        if (this.isEditView || !this.config.fromPage) {
            this._store.dispatch({
                type: DashboardActionTypes.SET_FOCUS_VALUE,
                payload: this.selectItem
            });
        }
    }

    private _setValueForFocus(focusInfo) {
        this.finderDS = [focusInfo];
        this.selectItem = focusInfo;
    }

    private _toggleDropdown(isOn = true) {
        if (this.finderEle) {
            this.finderEle.toggle(isOn);
        }
    }

    private _isShowFocus(t: ScreenFocusType) {
        return [ScreenFocusType.ENABLE_FOCUS_CONTROL].includes(t);
    }

    private _isShowOneDegree(t: Focus) {
        return !!t.enableOneDegree;
    }

    private _onSubscribeEvents(event) {
        switch (event.type) {
            case DashboardEditorSubChromeEventType.changeInfo:
                this.screenInfo = event.payload;
                break;
            case DashboardEditorSubChromeEventType.setFoucsValue:
                this._setValueForFocus(event.payload);
                break;
            case DashboardEditorSubChromeEventType.changeData: {
                this._screenData = event.payload;
                this.showFocus = this._isShowFocus(event.payload.configuration.focus.type);
                this.isOneDegreeShown = this._isShowOneDegree(event.payload.configuration.focus);
                if (event.payload.configuration.focus.entityTypes && event.payload.configuration.focus.entityTypes.length > 0) {
                    this._source.entityType = event.payload.configuration.focus.entityTypes;
                }
                break;
            }
            case DashboardEditorSubChromeEventType.setFocusValueStore:
                this._setFocusValueStore();
                break;
            case DashboardEditorSubChromeEventType.setLoadingStatus:
                this.saveBtnLoading = event.payload;
                break;
            case DashboardEditorSubChromeEventType.editTile:
                this.onDashboardEditClick(event.payload);
                break;
            default:
                break;
        }
    }
}
